import React, { useState, forwardRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Alert,
  Snackbar,
  useTheme,
  Typography,
  Slide,
  Divider,
  CircularProgress,
  Portal,
  Fade,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { tokens } from "../../theme";
import { restockProduct } from "../../services/ProductService";
import { Formik } from "formik";
import * as Yup from "yup";

// Transition for the Dialog (slides up)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transition for the Snackbar (slides in from the right)
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const RestockModal = ({
  open,
  handleClose,
  productId,
  product,
  onRestocked,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [phase, setPhase] = useState("idle");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Default to the product's source, or "Manufactured" if none exists.
  const initialValues = {
    quantity: "",
    sourceType: product?.sourceType || "Manufactured",
  };

  const validationSchema = Yup.object().shape({
    quantity: Yup.number()
      .typeError("Quantity must be a valid number")
      .required("Quantity is required")
      .moreThan(0, "Quantity must be greater than zero"),
    sourceType: Yup.string()
      .oneOf(["Purchased", "Manufactured"], "Invalid source")
      .required("Source Type is required"),
  });

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setPhase("loading");
    try {
      const payload = {
        quantity: values.quantity,
        sourceType: values.sourceType || null,
      };

      const id = productId || (product && product.id);
      if (!id) throw new Error("Product ID is required to restock");

      const response = await restockProduct(id, payload);

      // Keep loading state visible for 1.5 seconds to match other forms
      setTimeout(() => {
        setPhase("idle");
        resetForm();
        handleClose();

        if (onRestocked) {
          onRestocked(response.data);
        }

        // Show the snackbar slightly after the dialog closes
        setTimeout(() => {
          setSnackbarSeverity("success");
          setSnackbarMessage("Product restocked successfully!");
          setSnackbarOpen(true);
        }, 300);
      }, 1500);
    } catch (error) {
      console.error("Restock failed:", error);
      setPhase("idle");
      setSnackbarSeverity("error");
      const msg =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Failed to restock product. Please try again.";
      setSnackbarMessage(String(msg));
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Shared input styling to match the modern aesthetic
  const inputStyleProps = {
    "& .MuiFilledInput-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.04)",
      borderRadius: "8px",
      border: "1px solid transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        borderColor: colors.blueAccent[500],
      },
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        display: "none",
        WebkitAppearance: "none",
        margin: 0,
      },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  };

  const dropdownPaperStyle = {
    backgroundColor:
      theme.palette.mode === "dark"
        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
        : "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(16px)",
    border: `1px solid ${
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.15)"
        : "rgba(0, 0, 0, 0.1)"
    }`,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
    borderRadius: "12px",
    mt: 1,
    "& .MuiMenuItem-root": {
      padding: "12px 20px",
      margin: "4px 8px",
      borderRadius: "8px",
      transition: "all 0.2s ease-in-out",
      color: colors.gray[100],
      fontWeight: 600,
      fontSize: "14px",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.04)",
      },
      "&:active": { transform: "scale(0.95)" },
      "&.Mui-selected": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.15)"
            : "rgba(0, 0, 0, 0.08)",
        color: colors.blueAccent[500],
      },
      "&.Mui-selected:hover": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.12)",
      },
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (phase !== "loading") handleClose();
        }}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "12px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
          },
        }}
      >
        <DialogTitle
          textAlign="center"
          sx={{ fontSize: "1.25rem", fontWeight: "700", pt: 3, pb: 2 }}
        >
          Restock Product
        </DialogTitle>
        <Divider
          sx={{
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.12)",
          }}
        />
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent sx={{ p: 3 }}>
                <Typography
                  variant="body1"
                  color={colors.gray[100]}
                  sx={{ mb: 3, fontWeight: 500 }}
                >
                  {product?.productName ? (
                    <>
                      Restocking{" "}
                      <strong style={{ color: colors.blueAccent[500] }}>
                        {product.productName}
                      </strong>
                    </>
                  ) : (
                    "Enter restock details"
                  )}
                </Typography>

                <Box
                  display="grid"
                  gap="16px"
                  gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="number"
                    label="Quantity to Add"
                    name="quantity"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFieldValue("quantity", v === "" ? "" : Number(v));
                    }}
                    value={values.quantity}
                    error={!!touched.quantity && !!errors.quantity}
                    helperText={touched.quantity && errors.quantity}
                    inputProps={{ min: 1 }}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 2", sm: "span 1" },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      style: { borderRadius: "8px", fontSize: "0.95rem" },
                    }}
                    InputLabelProps={{ style: { fontSize: "0.95rem" } }}
                  />

                  <FormControl
                    fullWidth
                    variant="filled"
                    error={!!touched.sourceType && !!errors.sourceType}
                    sx={{
                      ...inputStyleProps,
                      gridColumn: { xs: "span 2", sm: "span 1" },
                    }}
                  >
                    <InputLabel
                      id="source-type-label"
                      style={{ fontSize: "0.95rem" }}
                    >
                      Source
                    </InputLabel>
                    <Select
                      labelId="source-type-label"
                      name="sourceType"
                      value={values.sourceType}
                      onChange={(event) => {
                        setFieldValue("sourceType", event.target.value);
                      }}
                      onBlur={handleBlur}
                      disableUnderline
                      sx={{ borderRadius: "8px", fontSize: "0.95rem" }}
                      MenuProps={{
                        TransitionComponent: Fade,
                        transitionDuration: 300,
                        PaperProps: { sx: dropdownPaperStyle },
                      }}
                    >
                      <MenuItem value="Manufactured">Manufactured</MenuItem>
                      <MenuItem value="Purchased">Purchased</MenuItem>
                    </Select>
                    {touched.sourceType && errors.sourceType && (
                      <FormHelperText>{errors.sourceType}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </DialogContent>

              <Divider
                sx={{
                  mb: 0,
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.12)",
                }}
              />

              <DialogActions
                sx={{
                  px: 3,
                  pb: 3,
                  pt: { xs: 2, sm: 3 },
                  gap: { xs: 2, sm: 2 },
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  justifyContent: { xs: "center", sm: "flex-end" },
                  "& > :not(style) ~ :not(style)": {
                    ml: "0 !important",
                  },
                }}
              >
                <Button
                  onClick={handleClose}
                  disabled={phase === "loading"}
                  variant="outlined"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    color: colors.gray[100],
                    borderColor: colors.gray[500],
                    textTransform: "none",
                    fontWeight: "600",
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1.2, sm: 1.2 },
                    borderRadius: "8px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      borderColor: colors.gray[300],
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.04)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={phase === "loading"}
                  variant="contained"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    backgroundColor: colors.blueAccent[600],
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: "600",
                    px: { xs: 2, sm: 4 },
                    py: { xs: 1.2, sm: 1.2 },
                    borderRadius: "8px",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 4px 10px rgba(0,0,0,0.3)"
                        : "0 4px 12px rgba(79, 70, 229, 0.2)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: colors.blueAccent[500],
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 6px 14px rgba(0,0,0,0.4)"
                          : "0 6px 16px rgba(79, 70, 229, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.12)"
                          : "rgba(0, 0, 0, 0.12)",
                      color:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.3)"
                          : "rgba(0, 0, 0, 0.26)",
                    },
                  }}
                >
                  {phase === "loading" ? (
                    <>
                      Saving&nbsp;
                      <CircularProgress
                        size={18}
                        sx={{ color: "inherit", ml: "8px" }}
                      />
                    </>
                  ) : (
                    "Restock"
                  )}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant="filled"
            sx={{
              width: "100%",
              backgroundColor:
                snackbarSeverity === "success"
                  ? colors.greenAccent[700]
                  : colors.redAccent[700],
              color: "#fff",
              fontWeight: "600",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default RestockModal;
